import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import {
  ClipboardList,
  Plus,
  Search,
  Send,
  RefreshCw,
  Trash2,
  Star,
  CheckCircle,
  Clock,
  Mail,
  ExternalLink,
  BarChart3,
  Users,
  Calendar,
  Truck,
  Trophy,
  TrendingUp,
  MessageSquare,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SurveysPage() {
  const { user } = useAuth();
  const [surveys, setSurveys] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  // Search states
  const [eventSearch, setEventSearch] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [searchedEvents, setSearchedEvents] = useState([]);
  const [searchedContacts, setSearchedContacts] = useState([]);
  const [foodTrucks, setFoodTrucks] = useState([]);
  
  // Form states
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedFoodTruck, setSelectedFoodTruck] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [includeGoogleReview, setIncludeGoogleReview] = useState(true);
  const [googleReviewUrl, setGoogleReviewUrl] = useState("");
  const [messagePersonnalise, setMessagePersonnalise] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const [kpiData, setKpiData] = useState(null);
  const [activeTab, setActiveTab] = useState("surveys"); // "surveys" or "kpi"

  const loadData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [surveysRes, statsRes, ftRes, kpiRes] = await Promise.all([
        axios.get(`${API_URL}/api/surveys`, { headers }),
        axios.get(`${API_URL}/api/surveys/stats`, { headers }),
        axios.get(`${API_URL}/api/food-trucks`, { headers }),
        axios.get(`${API_URL}/api/surveys/kpi/food-trucks`, { headers })
      ]);

      setSurveys(surveysRes.data);
      setStats(statsRes.data);
      setFoodTrucks(ftRes.data);
      setKpiData(kpiRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const searchEvents = async (query) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/api/surveys/search/events?q=${encodeURIComponent(query)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSearchedEvents(response.data);
    } catch (error) {
      console.error("Error searching events:", error);
    }
  };

  const searchContacts = async (query) => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (query) params.append("q", query);
      if (selectedEvent?.id) params.append("event_id", selectedEvent.id);
      
      const response = await axios.get(
        `${API_URL}/api/surveys/search/contacts?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSearchedContacts(response.data);
    } catch (error) {
      console.error("Error searching contacts:", error);
    }
  };

  const handleCreateSurvey = async () => {
    if (!selectedEvent || !selectedFoodTruck || !recipientEmail) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/surveys`,
        {
          event_id: selectedEvent.id,
          food_truck_id: selectedFoodTruck,
          recipient_email: recipientEmail,
          recipient_name: recipientName,
          include_google_review: includeGoogleReview,
          google_review_url: googleReviewUrl || null,
          message_personnalise: messagePersonnalise || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message || "Sondage créé avec succès !");
      setShowCreateDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la création du sondage");
    } finally {
      setSending(false);
    }
  };

  const handleResend = async (surveyId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/api/surveys/${surveyId}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Rappel envoyé !");
    } catch (error) {
      toast.error("Erreur lors de l'envoi du rappel");
    }
  };

  const handleDelete = async (surveyId) => {
    if (!window.confirm("Supprimer ce sondage ?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/surveys/${surveyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Sondage supprimé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const resetForm = () => {
    setSelectedEvent(null);
    setSelectedFoodTruck("");
    setSelectedContact(null);
    setRecipientEmail("");
    setRecipientName("");
    setIncludeGoogleReview(true);
    setGoogleReviewUrl("");
    setMessagePersonnalise("");
    setEventSearch("");
    setContactSearch("");
    setSearchedEvents([]);
    setSearchedContacts([]);
  };

  const selectContact = (contact) => {
    setSelectedContact(contact);
    setRecipientEmail(contact.email);
    setRecipientName(contact.name);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" /> Complété</Badge>;
      case "sent":
        return <Badge className="bg-blue-500"><Mail className="w-3 h-3 mr-1" /> Envoyé</Badge>;
      default:
        return <Badge className="bg-gray-500"><Clock className="w-3 h-3 mr-1" /> En attente</Badge>;
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          <Star
            key={i}
            className={`w-4 h-4 ${i <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // KPI Tab Content Component
  const KPIContent = () => (
    <div className="space-y-6">
      {/* Global KPI Cards */}
      {kpiData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-700">Food Trucks évalués</p>
                    <p className="text-3xl font-bold text-amber-900">{kpiData.total_food_trucks_evaluated}</p>
                  </div>
                  <Truck className="w-10 h-10 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-700">Note moyenne globale</p>
                    <p className="text-3xl font-bold text-green-900">{kpiData.overall_average}/5</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-700">Total évaluations</p>
                    <p className="text-3xl font-bold text-blue-900">{kpiData.total_responses}</p>
                  </div>
                  <MessageSquare className="w-10 h-10 text-blue-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top 5 Food Trucks */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Trophy className="w-5 h-5 text-yellow-500" />
                Top 5 des Food Trucks
              </CardTitle>
            </CardHeader>
            <CardContent>
              {kpiData.top_5.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Trophy className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Aucune évaluation disponible pour le moment</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {kpiData.top_5.map((ft, index) => (
                    <div 
                      key={ft.food_truck_id}
                      className={`flex items-center gap-4 p-4 rounded-lg ${
                        index === 0 ? "bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200" :
                        index === 1 ? "bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200" :
                        index === 2 ? "bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200" :
                        "bg-gray-50"
                      }`}
                    >
                      {/* Rank */}
                      <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold text-lg ${
                        index === 0 ? "bg-yellow-400 text-yellow-900" :
                        index === 1 ? "bg-gray-300 text-gray-700" :
                        index === 2 ? "bg-orange-300 text-orange-800" :
                        "bg-gray-200 text-gray-600"
                      }`}>
                        {index + 1}
                      </div>
                      
                      {/* Logo */}
                      {ft.logo_url ? (
                        <img src={ft.logo_url} alt={ft.nom} className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <Truck className="w-6 h-6 text-primary" />
                        </div>
                      )}
                      
                      {/* Info */}
                      <div className="flex-1">
                        <h3 className="font-semibold text-secondary">{ft.nom}</h3>
                        <p className="text-sm text-muted-foreground">{ft.specialite || "Food Truck"}</p>
                        <p className="text-xs text-muted-foreground">{ft.total_responses} évaluations</p>
                      </div>
                      
                      {/* Score */}
                      <div className="text-right">
                        <div className="flex items-center gap-1">
                          {renderStars(Math.round(ft.global_average))}
                        </div>
                        <p className="text-2xl font-bold text-primary">{ft.global_average}/5</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* All Food Trucks Table */}
          {kpiData.all_food_trucks.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Détail par Food Truck
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Food Truck</TableHead>
                      <TableHead className="text-center">Ponctualité</TableHead>
                      <TableHead className="text-center">Service</TableHead>
                      <TableHead className="text-center">Qualité</TableHead>
                      <TableHead className="text-center">Hygiène</TableHead>
                      <TableHead className="text-center">Prix</TableHead>
                      <TableHead className="text-center">Global</TableHead>
                      <TableHead className="text-center">Éval.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {kpiData.all_food_trucks.map((ft) => (
                      <TableRow key={ft.food_truck_id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {ft.logo_url ? (
                              <img src={ft.logo_url} alt={ft.nom} className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <Truck className="w-4 h-4 text-primary" />
                              </div>
                            )}
                            <span className="font-medium">{ft.nom}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={ft.averages.ponctualite >= 4 ? "text-green-600 font-semibold" : ft.averages.ponctualite >= 3 ? "text-yellow-600" : "text-red-600"}>
                            {ft.averages.ponctualite}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={ft.averages.service >= 4 ? "text-green-600 font-semibold" : ft.averages.service >= 3 ? "text-yellow-600" : "text-red-600"}>
                            {ft.averages.service}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={ft.averages.qualite >= 4 ? "text-green-600 font-semibold" : ft.averages.qualite >= 3 ? "text-yellow-600" : "text-red-600"}>
                            {ft.averages.qualite}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={ft.averages.hygiene >= 4 ? "text-green-600 font-semibold" : ft.averages.hygiene >= 3 ? "text-yellow-600" : "text-red-600"}>
                            {ft.averages.hygiene}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={ft.averages.prix >= 4 ? "text-green-600 font-semibold" : ft.averages.prix >= 3 ? "text-yellow-600" : "text-red-600"}>
                            {ft.averages.prix}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={ft.global_average >= 4 ? "bg-green-500" : ft.global_average >= 3 ? "bg-yellow-500" : "bg-red-500"}>
                            {ft.global_average}/5
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center text-muted-foreground">
                          {ft.total_responses}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="surveys-page">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <h1 className="text-2xl font-heading font-bold text-secondary">
          Sondages & Évaluations
        </h1>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-primary hover:bg-primary/90">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau sondage
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-gray-200 pb-2">
        <Button
          variant={activeTab === "surveys" ? "default" : "ghost"}
          onClick={() => setActiveTab("surveys")}
          className={activeTab === "surveys" ? "bg-primary" : ""}
        >
          <ClipboardList className="w-4 h-4 mr-2" />
          Sondages
        </Button>
        <Button
          variant={activeTab === "kpi" ? "default" : "ghost"}
          onClick={() => setActiveTab("kpi")}
          className={activeTab === "kpi" ? "bg-primary" : ""}
        >
          <Trophy className="w-4 h-4 mr-2" />
          KPI Food Trucks
        </Button>
      </div>

      {/* Tab Content */}
      {activeTab === "kpi" ? (
        <KPIContent />
      ) : (
        <>
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Réponses</p>
                      <p className="text-2xl font-bold">{stats.total_responses}</p>
                    </div>
                    <Users className="w-8 h-8 text-primary opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Note moyenne</p>
                      <p className="text-2xl font-bold">{stats.averages?.global || "-"}/5</p>
                    </div>
                    <Star className="w-8 h-8 text-yellow-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Clics Google</p>
                      <p className="text-2xl font-bold">{stats.google_review_clicks}</p>
                    </div>
                    <ExternalLink className="w-8 h-8 text-blue-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Taux réponse</p>
                      <p className="text-2xl font-bold">
                        {surveys.length > 0 
                          ? Math.round((stats.total_responses / surveys.length) * 100) 
                          : 0}%
                      </p>
                    </div>
                    <BarChart3 className="w-8 h-8 text-green-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

      {/* Average Scores */}
      {stats?.averages && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Moyennes par critère
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { key: "ponctualite", label: "Ponctualité" },
                { key: "service", label: "Service" },
                { key: "qualite", label: "Qualité" },
                { key: "hygiene", label: "Hygiène" },
                { key: "prix", label: "Prix" }
              ].map(({ key, label }) => (
                <div key={key} className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">{label}</p>
                  <p className="text-xl font-bold">{stats.averages[key]}/5</p>
                  <div className="flex justify-center mt-1">
                    {renderStars(Math.round(stats.averages[key]))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Surveys List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary" />
            Liste des sondages
          </CardTitle>
        </CardHeader>
        <CardContent>
          {surveys.length === 0 ? (
            <div className="text-center py-8">
              <ClipboardList className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucun sondage créé</p>
              <p className="text-sm text-muted-foreground">
                Créez votre premier sondage pour évaluer vos prestations
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Événement</TableHead>
                  <TableHead>Food Truck</TableHead>
                  <TableHead>Destinataire</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {surveys.map(survey => (
                  <TableRow key={survey.id}>
                    <TableCell>
                      <div className="font-medium">{survey.event?.titre || "N/A"}</div>
                      {survey.event?.date_debut && (
                        <div className="text-xs text-muted-foreground">
                          {format(parseISO(survey.event.date_debut), "dd/MM/yyyy", { locale: fr })}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{survey.food_truck?.nom_food_truck || "N/A"}</TableCell>
                    <TableCell>
                      <div>{survey.recipient_name || "-"}</div>
                      <div className="text-xs text-muted-foreground">{survey.recipient_email}</div>
                    </TableCell>
                    <TableCell>{getStatusBadge(survey.status)}</TableCell>
                    <TableCell>
                      {survey.response ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-1">
                            <span className="text-xs">Moy:</span>
                            {renderStars(Math.round(
                              (survey.response.ponctualite + survey.response.service + 
                               survey.response.qualite + survey.response.hygiene + 
                               survey.response.prix) / 5
                            ))}
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {format(parseISO(survey.created_at), "dd/MM/yyyy", { locale: fr })}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {survey.status !== "completed" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleResend(survey.id)}
                            title="Renvoyer"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(survey.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
        </>
      )}

      {/* Create Survey Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        setShowCreateDialog(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-primary" />
              Nouveau sondage
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Event Search */}
            <div>
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Événement *
              </Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={eventSearch}
                  onChange={(e) => {
                    setEventSearch(e.target.value);
                    if (e.target.value.length > 2) searchEvents(e.target.value);
                  }}
                  placeholder="Rechercher un événement..."
                />
                <Button variant="outline" onClick={() => searchEvents(eventSearch)}>
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              {selectedEvent && (
                <div className="mt-2 p-2 bg-green-50 rounded flex items-center justify-between">
                  <div>
                    <span className="font-medium">{selectedEvent.titre}</span>
                    <span className="text-sm text-muted-foreground ml-2">
                      {selectedEvent.date_debut && format(parseISO(selectedEvent.date_debut), "dd/MM/yyyy")}
                    </span>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedEvent(null)}>×</Button>
                </div>
              )}
              {searchedEvents.length > 0 && !selectedEvent && (
                <div className="mt-2 border rounded max-h-40 overflow-y-auto">
                  {searchedEvents.map(event => (
                    <div
                      key={event.id}
                      className="p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                      onClick={() => {
                        setSelectedEvent(event);
                        setSearchedEvents([]);
                        searchContacts("");
                      }}
                    >
                      <div className="font-medium">{event.titre}</div>
                      <div className="text-xs text-muted-foreground">
                        {event.date_debut && format(parseISO(event.date_debut), "dd/MM/yyyy")} - {event.lieu}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Food Truck Select */}
            <div>
              <Label className="flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Food Truck à évaluer *
              </Label>
              <Select value={selectedFoodTruck} onValueChange={setSelectedFoodTruck}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Sélectionner un food truck" />
                </SelectTrigger>
                <SelectContent>
                  {foodTrucks.map(ft => (
                    <SelectItem key={ft.id} value={ft.id}>
                      {ft.nom_food_truck}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Contact Search */}
            <div>
              <Label className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Destinataire *
              </Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={contactSearch}
                  onChange={(e) => {
                    setContactSearch(e.target.value);
                    if (e.target.value.length > 2) searchContacts(e.target.value);
                  }}
                  placeholder="Rechercher un contact..."
                />
                <Button variant="outline" onClick={() => searchContacts(contactSearch)}>
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              {searchedContacts.length > 0 && (
                <div className="mt-2 border rounded max-h-40 overflow-y-auto">
                  {searchedContacts.map(contact => (
                    <div
                      key={contact.id}
                      className="p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                      onClick={() => {
                        selectContact(contact);
                        setSearchedContacts([]);
                      }}
                    >
                      <div className="font-medium">{contact.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {contact.email} {contact.company && `- ${contact.company}`}
                      </div>
                      <Badge variant="secondary" className="text-xs mt-1">
                        {contact.source === "event" ? "Contact événement" : "CRM"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <Label className="text-xs">Email *</Label>
                  <Input
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="email@exemple.com"
                    type="email"
                  />
                </div>
                <div>
                  <Label className="text-xs">Nom</Label>
                  <Input
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    placeholder="Nom du contact"
                  />
                </div>
              </div>
            </div>

            {/* Google Review */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="google-review"
                  checked={includeGoogleReview}
                  onCheckedChange={setIncludeGoogleReview}
                />
                <Label htmlFor="google-review" className="cursor-pointer">
                  Inclure une demande d'avis Google
                </Label>
              </div>
              {includeGoogleReview && (
                <Input
                  value={googleReviewUrl}
                  onChange={(e) => setGoogleReviewUrl(e.target.value)}
                  placeholder="URL de la page Google Business (optionnel)"
                />
              )}
            </div>

            {/* Custom Message */}
            <div>
              <Label>Message personnalisé (optionnel)</Label>
              <Textarea
                value={messagePersonnalise}
                onChange={(e) => setMessagePersonnalise(e.target.value)}
                placeholder="Ajoutez un message personnalisé à l'email..."
                rows={3}
              />
            </div>

            <Button 
              onClick={handleCreateSurvey} 
              disabled={sending || !selectedEvent || !selectedFoodTruck || !recipientEmail}
              className="w-full"
            >
              {sending ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Envoi en cours...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Envoyer le sondage</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
